import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  LoadingOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import type { UploadFile } from 'antd'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Divider,
  Typography,
  Upload
} from 'antd'
import { AxiosError } from 'axios'
import type { UploadRequestOption } from 'rc-upload/lib/interface'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import $api from '../../../../API'
import { EmptyCellValue } from './empty-cell-value'

interface PaymentFileData {
  type: 'payment-file'

  fileGroupName: string
  fileGroupNumber: string

  date: string
  lines: string[]

  d: unknown[]
  h: unknown[]
  s: unknown[]
  t: unknown[]
}

interface CrTotalFileData {
  type: 'cr-total-file'

  fileGroupName: string
  fileGroupNumber: string
}

type Response = SuccessResponse | FailedResponse

interface SuccessResponse {
  ok: true

  file: {
    originalname: string
    mimetype: string
    size: number
  }

  parsedData: PaymentFileData | CrTotalFileData
}

interface FailedResponse {
  ok: false

  error: {
    message: string
  }
}

interface FilePickerProps {
  onCancel: () => void
}

export function FilePicker(props: FilePickerProps): JSX.Element {
  const { notification } = AntdApp.useApp()

  const [fileList, setFileList] = useState<UploadFile<Response>[]>([])
  const [uploadingError, setUploadingError] = useState<string | undefined>()
  const [duplicationError, setDuplicationError] = useState<string | undefined>()

  const mutation = useMutation({
    mutationFn: (files: UploadFile<Response>[]) => {
      const formData = new FormData()

      files.forEach(file => {
        formData.append('files', file.originFileObj as Blob)
      })

      return $api.post('/payments/upload', formData, {
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    },

    onSuccess() {
      setFileList([])

      props.onCancel()

      notification.success({
        message: 'Upload',
        description: `${fileList.length} file(s) have been uploaded successfully`,
        showProgress: true,
        pauseOnHover: true
      })
    },

    onError(error: unknown) {
      const defaultErrorMessage = 'Unknown Error'

      let errorMessage = defaultErrorMessage

      if (error instanceof AxiosError) {
        errorMessage =
          error.response?.data?.error?.message ?? error.response?.statusText ?? defaultErrorMessage
      } else if (error instanceof Error) {
        errorMessage = error.message ?? defaultErrorMessage
      }

      setUploadingError(errorMessage)

      notification.error({
        message: 'Upload Failed',
        description: errorMessage,
        showProgress: true,
        pauseOnHover: true
      })
    }
  })

  useEffect(() => {
    if (uploadingError && fileList.length === 0) {
      setUploadingError(undefined)
    }

    if (duplicationError && fileList.length === 0) {
      setDuplicationError(undefined)
    }
  }, [uploadingError, duplicationError, fileList])

  const handleUploadClick = useCallback(() => {
    setUploadingError(undefined)
    setDuplicationError(undefined)

    mutation.mutate(fileList)
  }, [mutation, fileList])

  const handleChange = useCallback(
    (info: { fileList: UploadFile<Response>[]; file: UploadFile<Response> }) => {
      const hasFileListDuplicates = info.fileList
        .map(file => file.name)
        .some(
          (filename, index) => index !== info.fileList.findIndex(file => file.name === filename)
        )

      if (info.fileList.length > 1 && hasFileListDuplicates) {
        setDuplicationError(
          'You are trying to upload a file that already exists in the list, remove it and try again'
        )

        setFileList(
          info.fileList.filter(
            (file, index) => info.fileList.findIndex(f => f.name === file.name) === index
          )
        )

        return
      }

      setFileList(info.fileList)

      if (uploadingError) {
        setUploadingError(undefined)
      }
    },
    [uploadingError]
  )

  const handleRemove = useCallback(
    (file: UploadFile<Response>) => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid))

      if (uploadingError) {
        setUploadingError(undefined)
      }

      if (duplicationError) {
        setDuplicationError(undefined)
      }
    },
    [uploadingError, duplicationError]
  )

  const handleRequest = async (options: UploadRequestOption<Response>) => {
    const formData = new FormData()

    formData.append('file', options.file)

    try {
      const response = await $api.post<Response>('/payments/preupload', formData, {
        method: 'POST'
      })

      if (response.data.ok) {
        options.onSuccess?.(response.data)
      } else {
        options.onError?.(new Error(response.data.error.message))
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        options.onError?.(error)
      }
    }
  }

  const handleUploadFailedAlertClose = () => {
    setUploadingError(undefined)
  }

  const handleDuplicationAlertClose = () => {
    setDuplicationError(undefined)
  }

  const hasInvalidFiles = fileList.some(file => file.status === 'error')
  const hasValidatingFiles = fileList.some(file => file.status === 'uploading')
  const isEmptyFileList = fileList.length === 0
  const isUploadingFiles = mutation.isPending

  const itemRender = (_originNode: ReactElement, file: UploadFile<Response>) => {
    return (
      <FileItem options={file} isUploading={isUploadingFiles} onRemove={() => handleRemove(file)} />
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      <Upload.Dragger
        height={400}
        disabled={isUploadingFiles}
        multiple
        fileList={fileList}
        onChange={handleChange}
        onRemove={handleRemove}
        itemRender={itemRender}
        customRequest={handleRequest}
      >
        <p className='ant-upload-drag-icon'>
          <InboxOutlined />
        </p>

        <p className='ant-upload-text'>Click or drag file to this area to upload</p>

        <p className='ant-upload-hint'>
          Support only payment files (example cl250101.000, re250101.000) and CR total files
          (example cr_000000_20250101.pdf).
        </p>

        <p className='ant-upload-hint'>Maximum upload file size: 300MB.</p>
      </Upload.Dragger>

      {uploadingError && (
        <Alert
          message='Upload Failed'
          description={uploadingError}
          type='error'
          showIcon
          closable
          onClose={handleUploadFailedAlertClose}
        />
      )}

      {duplicationError && (
        <Alert
          message='Validation Failed'
          description={duplicationError}
          type='error'
          showIcon
          closable
          onClose={handleDuplicationAlertClose}
        />
      )}

      <div className='flex justify-end'>
        <div className='flex gap-2'>
          <Button disabled={isUploadingFiles} onClick={props.onCancel}>
            Close
          </Button>

          <Button
            type='primary'
            disabled={isEmptyFileList || isUploadingFiles || hasValidatingFiles || hasInvalidFiles}
            onClick={handleUploadClick}
            icon={isUploadingFiles ? <LoadingOutlined className='mb-2' /> : <UploadOutlined />}
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  )
}

interface FileItemProps {
  options: UploadFile<Response>
  isUploading: boolean
  onRemove: () => void
}

const FileItem = (props: FileItemProps): JSX.Element => {
  const handleRemove = () => {
    props.onRemove()
  }

  return (
    <Card
      className='mt-4'
      styles={{
        header: {
          borderBottom:
            props.options.status && !['done', 'error'].includes(props.options.status)
              ? 'none'
              : undefined
        },
        body: {
          padding: 0
        }
      }}
      title={
        <div className='flex justify-between items-center'>
          <div className='flex gap-2 items-center'>
            {props.options.status === 'uploading' && <LoadingOutlined />}
            {props.options.status === 'done' && <CheckCircleOutlined className='text-green-600' />}
            {props.options.status === 'error' && (
              <ExclamationCircleOutlined className='text-red-600' />
            )}

            <span>{props.options.name}</span>
          </div>

          <div className='flex gap-2 items-center'>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              disabled={props.isUploading}
            />
          </div>
        </div>
      }
    >
      {props.options.status === 'error' && (
        <div className='p-4'>
          <Descriptions size='small' column={1}>
            <Descriptions.Item label='Error'>
              <Typography.Text>
                {Object.hasOwn(props.options.error?.response?.data, 'ok')
                  ? props.options.error?.response?.data.error.message
                  : props.options.error?.message}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {props.options.status === 'done' && props.options.response?.ok && (
        <div className='p-4'>
          <Descriptions size='small' column={1}>
            <Descriptions.Item label='MIME Type'>
              <Typography.Text>
                {props.options.response?.file?.mimetype || 'text/plain'}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label='File Size'>
              <Typography.Text>
                {props.options.response?.file?.size ? (
                  `${(props.options.response?.file?.size / 1024).toFixed(2)} KB`
                ) : (
                  <EmptyCellValue />
                )}
              </Typography.Text>
            </Descriptions.Item>

            {props.options.response?.parsedData &&
              props.options.response.parsedData.type === 'payment-file' && (
                <>
                  <Descriptions.Item>
                    <Divider className='m-0' />
                  </Descriptions.Item>

                  <Descriptions.Item label='Group Number'>
                    <Typography.Text>
                      {props.options.response.parsedData?.fileGroupNumber || <EmptyCellValue />}
                    </Typography.Text>
                  </Descriptions.Item>

                  <Descriptions.Item label='Group Name'>
                    <Typography.Text>
                      {props.options.response.parsedData?.fileGroupNumber ===
                      props.options.response.parsedData?.fileGroupName ? (
                        <EmptyCellValue />
                      ) : (
                        props.options.response.parsedData?.fileGroupName || <EmptyCellValue />
                      )}
                    </Typography.Text>
                  </Descriptions.Item>
                </>
              )}

            {props.options.response?.parsedData &&
              props.options.response.parsedData.type === 'cr-total-file' && (
                <>
                  <Descriptions.Item>
                    <Divider className='m-0' />
                  </Descriptions.Item>

                  <Descriptions.Item label='Group Number'>
                    <Typography.Text>
                      {props.options.response.parsedData?.fileGroupNumber || <EmptyCellValue />}
                    </Typography.Text>
                  </Descriptions.Item>

                  <Descriptions.Item label='Group Name'>
                    <Typography.Text>
                      {props.options.response.parsedData?.fileGroupNumber ===
                      props.options.response.parsedData?.fileGroupName ? (
                        <EmptyCellValue />
                      ) : (
                        props.options.response.parsedData?.fileGroupName || <EmptyCellValue />
                      )}
                    </Typography.Text>
                  </Descriptions.Item>
                </>
              )}
          </Descriptions>
        </div>
      )}
    </Card>
  )
}
